function _toConsumableArray(arr) { return _arrayWithoutHoles(arr) || _iterableToArray(arr) || _unsupportedIterableToArray(arr) || _nonIterableSpread(); }

function _nonIterableSpread() { throw new TypeError("Invalid attempt to spread non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method."); }

function _unsupportedIterableToArray(o, minLen) { if (!o) return; if (typeof o === "string") return _arrayLikeToArray(o, minLen); var n = Object.prototype.toString.call(o).slice(8, -1); if (n === "Object" && o.constructor) n = o.constructor.name; if (n === "Map" || n === "Set") return Array.from(o); if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray(o, minLen); }

function _iterableToArray(iter) { if (typeof Symbol !== "undefined" && Symbol.iterator in Object(iter)) return Array.from(iter); }

function _arrayWithoutHoles(arr) { if (Array.isArray(arr)) return _arrayLikeToArray(arr); }

function _arrayLikeToArray(arr, len) { if (len == null || len > arr.length) len = arr.length; for (var i = 0, arr2 = new Array(len); i < len; i++) { arr2[i] = arr[i]; } return arr2; }

import { DateTime, Interval } from 'luxon';
var CALENDAR_WEEKS = 6; // Weeks displayed on calendar

/**
 * Get previous month
 * Gets the month and year before the given month and year
 *
 * @param {luxon.DateTime} date - A date value
 *
 * @return {luxon.DateTime}
 */

export var getPreviousMonth = function getPreviousMonth(date) {
  return date.minus({
    month: 1
  });
};
/**
 * Get next month
 * Gets the month and year after the given month and year
 *
 * @param {luxon.DateTime} date - A date value
 *
 * @return {luxon.DateTime}
 */

export var getNextMonth = function getNextMonth(date) {
  return date.plus({
    month: 1
  });
};
/**
 * Is same day
 * Checks if two date values are the same day
 *
 * @param {luxon.DateTime} date - A date value
 * @param {luxon.DateTime} currentDate - A date value
 *
 * @return {boolean}
 */

export var isSameDay = function isSameDay(date, currentDate) {
  return date.hasSame(currentDate, 'day');
};
/**
 * Is same month
 * Checks if two date values are the in the same month
 *
 * @param {luxon.DateTime} date - A date value
 * @param {luxon.DateTime} currentDate - A date value
 *
 * @return {boolean}
 */

export var isSameMonth = function isSameMonth(date, currentDate) {
  return date.hasSame(currentDate, 'month');
};
/**
 * Is in next month
 * Whether the selected date is in the next month w.r.t. the whole calendar
 *
 * @param {luxon.DateTime} date - A date value
 * @param {luxon.DateTime} currentDate - A date value
 *
 * @return {boolean}
 */

export var isInNextMonth = function isInNextMonth(date, currentDate) {
  var monthStart = date.startOf('month');
  return monthStart.hasSame(currentDate.plus({
    month: 1
  }), 'month');
};
/**
 * Is in pervious month
 * Whether the selected date is in the pervious month
 *
 * @param {luxon.DateTime} date - A date value
 * @param {luxon.DateTime} currentDate - A date value
 *
 * @return {boolean}
 */

export var isInPrevMonth = function isInPrevMonth(date, currentDate) {
  var monthEnd = date.endOf('month');
  return monthEnd.hasSame(currentDate.minus({
    month: 1
  }), 'month');
};
/**
 * Is interval
 * Check if an object is an Interval
 *
 * @param {object} value
 *
 * @return {boolean}
 */

export var isInterval = function isInterval(value) {
  return Interval.isInterval(value);
};
/**
 * Calendar builder for a month in the specified year
 * Returns an array of the calendar dates.
 * Each calendar date is represented as an object => {YYYY, MM, DD}
 *
 * @param {luxon.DateTime} date - A date value
 *
 * @return {array}
 */

export var getCalendarDates = function getCalendarDates(date) {
  var daysInMonth = date.daysInMonth,
      year = date.year,
      month = date.month; // Get the month's first day

  var startOfMonth = date.startOf('month');
  var monthFirstDay = DateTime.fromISO(startOfMonth).weekday; // Get number of days to be displayed from previous and next months
  // These ensure a total of 42 days (6 weeks) displayed on the calendar

  var daysFromPrevMonth = monthFirstDay - 1;
  var daysFromNextMonth = CALENDAR_WEEKS * 7 - (daysFromPrevMonth + daysInMonth); // Get the previous and next months and years

  var _getPreviousMonth = getPreviousMonth(date),
      prevMonth = _getPreviousMonth.month,
      prevMonthYear = _getPreviousMonth.year;

  var _getNextMonth = getNextMonth(date),
      nextMonth = _getNextMonth.month,
      nextMonthYear = _getNextMonth.year; // Get number of days in previous month


  var prevMonthDays = date.minus({
    month: 1
  }).daysInMonth; // Builds dates to be displayed from previous month

  var prevMonthDates = _toConsumableArray(new Array(daysFromPrevMonth)).map(function (n, index) {
    var day = index + 1 + (prevMonthDays - daysFromPrevMonth);
    return {
      year: prevMonthYear,
      month: prevMonth,
      day: day
    };
  }); // Builds dates to be displayed from current month


  var thisMonthDates = _toConsumableArray(new Array(daysInMonth)).map(function (n, index) {
    var day = index + 1;
    return {
      year: year,
      month: month,
      day: day
    };
  }); // Builds dates to be displayed from next month


  var nextMonthDates = _toConsumableArray(new Array(daysFromNextMonth)).map(function (n, index) {
    var day = index + 1;
    return {
      year: nextMonthYear,
      month: nextMonth,
      day: day
    };
  }); // Combines all dates from previous, current and next months


  return [].concat(_toConsumableArray(prevMonthDates), _toConsumableArray(thisMonthDates), _toConsumableArray(nextMonthDates));
};
/**
 * Returns date bounded to optional min or max
 *
 * @param {DateTime} date - the date to bound
 * @param {DateTime} minDate - minimum date
 * @param {DateTime} maxDate - maximum date
 *
 * @returns {DateTime}
 */

export var minMaxDate = function minMaxDate(date, minDate, maxDate) {
  var minBoundedDate = minDate ? DateTime.max(minDate, date) : date;
  var boundedDate = maxDate ? DateTime.min(maxDate, minBoundedDate) : minBoundedDate;
  return boundedDate;
};